import { Box, Flex } from '@chakra-ui/react';
import Image from 'next/image';
import { useMemo } from 'react';
import { Heading } from 'ui-components';
import { Platform } from '~/screens/AddTalent/constants';
import {
  GenderDonut,
  Legend,
  useInsights,
} from '~/screens/CreatorAudienceDemographics/GenderDistribution';

import PlaceholderGender from '../static/gender.png';

export type DistributionProps = {
  insightId: string;
  platform: Platform;
  isConnected: boolean;
  isModal?: boolean;
};

export const GenderDistribution = ({
  insightId,
  platform,
  isConnected,
}: DistributionProps) => {
  const insights = useInsights({ insightId, platform });

  const genderValue = useMemo(() => {
    const genders = insights?.data?.genders;
    const male = genders?.find((g) => g.label.toLowerCase() === 'male');
    const female = genders?.find((g) => g.label.toLowerCase() === 'female');
    const other = genders?.find(
      (g) =>
        g.label.toLowerCase() === 'other' ||
        g.label.toLowerCase() === 'unknown',
    );

    return {
      male: Math.round((male?.value || 0) * 100),
      female: Math.round((female?.value || 0) * 100),

      // keep `other` if the value is a number. Applies to tiktok & snapchat insights
      other:
        typeof other?.value === 'number'
          ? Math.round(other.value * 100)
          : undefined,
    };
  }, [insights?.data]);

  if (!isConnected) {
    return <Image src={PlaceholderGender} alt="gender distribution" />;
  }

  return (
    <>
      <Heading size="sm">Gender distribution</Heading>
      <Flex gap={8} alignItems="center" height="100%">
        <Box width={220}>
          <GenderDonut values={genderValue} />
        </Box>
        <Box>
          <Flex direction="column" gap={8}>
            <Legend gender="Male" value={genderValue.male} />
            <Legend gender="Female" value={genderValue.female} />
            {typeof genderValue.other === 'number' ? (
              <Legend gender="Other" value={genderValue.other} />
            ) : null}
          </Flex>
        </Box>
      </Flex>
    </>
  );
};
